import { entityViewInset } from '@/components/common/EntityView';
import { FlexCol, FlexRow } from '@/components/common/Flex';
import { Shield01, TrendUp01 } from '@/components/common/icons';
import { Header, Paragraph, Text } from '@/components/common/Typography';
import {
    FlexProps,
    Grid,
    Slider,
    SliderFilledTrack,
    SliderThumb,
    SliderTrack,
} from '@chakra-ui/react';
import { useCurrentRateInfo } from '@common/react/rates';
import { GoalAccountInvestmentType } from '@common/types';
import {
    GoalSectorsPieChart,
    GoalSectorsPieChartLegend,
} from './../GoalSectorsPieChart';
import { riskLabelByRiskScore } from './../helpers';

interface OpenIraFlowRiskSelectionContentProps {
    title?: string;
    description: string;
    riskScore: number;
    /**
     * IMPORTANT: Only pass recommended risk scores from Atomic!
     * If none is available, pass `null` or `undefined`.
     */
    recommendedRiskScore: number | null | undefined;
    minRiskScore: 0 | 1;
    onChangeRiskScore: (riskScore: number) => void;
    investmentType: 'DIRECT_INDEXING'; // TODO: Support other types
}

// TODO: Try to reuse the RiskSelectionContent component and eliminate this one
export const OpenIraFlowRiskSelectionContent = ({
    title,
    description,
    riskScore,
    recommendedRiskScore,
    minRiskScore,
    onChangeRiskScore,
    investmentType,
}: OpenIraFlowRiskSelectionContentProps) => {
    const { currentRateInfo } = useCurrentRateInfo();
    if (!currentRateInfo) return null;

    const isRecommended = riskScore === recommendedRiskScore;
    const riskLabel = riskLabelByRiskScore[riskScore];

    if (riskScore < minRiskScore) {
        // This should never happen
        console.warn(`riskScore ${riskScore} < minRiskScore ${minRiskScore}`);
        return null;
    }

    return (
        <>
            <FlexRow grow={1}>
                <FlexCol grow={1} basis={0}>
                    <Header mb={2}>{title}</Header>
                    <Paragraph color="textMuted">{description}</Paragraph>
                    <Grid mt={10} templateColumns={'repeat(2, 1fr)'} gap={8}>
                        <FlexCol justify="flex-end">
                            <Text
                                color="green.700"
                                fontSize="xs"
                                fontWeight="semibold"
                                letterSpacing="widest"
                                mb={4}
                                opacity={isRecommended ? 1 : 0}
                                transition="opacity"
                                transitionDuration="200ms"
                            >
                                RECOMMENDED
                            </Text>
                            <Header color="gray.400" mb={2}>
                                <Text fontSize="3xl" color="blue.500">
                                    {riskScore}
                                </Text>
                                {' / 10'}
                            </Header>
                            <Text fontSize="lg">{riskLabel}</Text>
                        </FlexCol>
                    </Grid>
                </FlexCol>
                <PieChartSection
                    investmentType={investmentType}
                    riskScore={riskScore}
                />
            </FlexRow>
            <RiskSlider
                value={riskScore}
                onChange={onChangeRiskScore}
                min={minRiskScore}
            />
        </>
    );
};

interface PieChartSectionProps extends FlexProps {
    investmentType: GoalAccountInvestmentType;
    riskScore: number;
}

export const PieChartSection = ({
    investmentType,
    riskScore,
    ...props
}: PieChartSectionProps) => {
    return (
        <FlexRow w={96} pl={entityViewInset} {...props}>
            <FlexCol w="full" align="center">
                <GoalSectorsPieChart
                    investmentType={investmentType}
                    riskScore={riskScore}
                />
                <GoalSectorsPieChartLegend
                    investmentType={investmentType}
                    riskScore={riskScore}
                    layoutDirection="horizontal"
                    mt={10}
                />
            </FlexCol>
        </FlexRow>
    );
};

interface RiskSliderProps extends Omit<FlexProps, 'onChange'> {
    value: number;
    onChange?: (value: number) => void;
    min?: number;
    readOnly?: boolean;
}

export const RiskSlider = ({
    value,
    onChange,
    min = 0,
    readOnly = false,
    ...props
}: RiskSliderProps) => {
    return (
        <FlexRow
            w="full"
            mt={12}
            py={4}
            px={6}
            gap={6}
            bg="gray.50"
            borderRadius="xl"
            {...props}
        >
            <FlexCol alignItems="center" gap={2}>
                <Shield01 color="textMuted" />
                <Text fontSize="sm" color="textMuted" textAlign="center">
                    Lowest
                    <br />
                    risk
                </Text>
            </FlexCol>
            <FlexRow grow={1}>
                <Slider
                    value={value}
                    onChange={onChange}
                    min={min}
                    max={10}
                    step={1}
                >
                    <SliderTrack bg="gray.200">
                        <SliderFilledTrack bg="blue.400" />
                    </SliderTrack>
                    {!readOnly && <SliderThumb boxSize={5} bg="blue.400" />}
                </Slider>
            </FlexRow>
            <FlexCol alignItems="center" gap={2}>
                <TrendUp01 color="textMuted" />
                <Text fontSize="sm" color="textMuted" textAlign="center">
                    Higher
                    <br />
                    risk
                </Text>
            </FlexCol>
        </FlexRow>
    );
};
